import { getPlans } from 'actions/plans';
import useTranslation from 'hooks/useTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BannerInfo from './BannerInfo';
import DropshippingPlan from './DropshippingPlan';
import Faq from './Faq';
import style from './index.style';
import Promo from './Promo';
import { useRouter } from 'next/router';
import UpsellAidify from '../upsell-aidify';
import TurnkeyPlan from './TurnkeyPlan';
import Button from '../utils/Button';

const TABS = {
  DROPSHIPPING: 'dropshipping',
  TURNKEY: 'turnkey',
  BSOCIAL: 'bsocial',
};

const PERIODICITY = {
  MONTHLY: 0,
  YEARLY: 1,
};

const INITIAL_RADIO_OPTIONS = {
  dropshipping: {
    year: false,
    month: false,
  },
  turnkey: {
    year: false,
    month: false,
  },
  bsocial: {
    year: false,
    month: false,
  },
};

const shopify = {
  attributes: {
    role: 'shopify',
    turnkey: false,
  },
  code: 'shopify',
  planCode: 'shopify',
  descriptions: {
    en_US: '',
    es_ES: '',
    fr_FR: '',
    it_IT: '',
  },
  id: 0,
  model: 'month',
  name: 'Shopify',
  size: 'month',
  streetPrice: null,
  suggestedPrice: 99,
  _id: '12345',
};

export default function Pricing({ type }) {
  const { TRANSLATIONS } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const locale = useSelector((state) => state.locale);
  const { tab } = router.query;
  const [selectedTab, setSelectedTab] = useState(tab || TABS.DROPSHIPPING);
  const plansSetting =
    useSelector((state) => state.setting)?.find(
      (setting) => setting.type === 'PlansSetting'
    )?.body || [];
  const user = useSelector((state) => state.user);
  const currency = user?.countryCode === 'US' ? 'USD' : 'EUR';
  const catalogs = useSelector((state) => state?.user?.userCatalogs) || [];
  const { lang } = router?.query;
  const isLoggedIn = useSelector((s) => s.isLoggedIn);

  useEffect(() => {
    setSelectedTab(tab || TABS.DROPSHIPPING);
  }, [tab]);

  const promoActive = plansSetting?.promoStatus;

  const switchYearly = plansSetting?.switchYearly;

  const language = useMemo(
    () =>
      locale === 'it_IT'
        ? 'IT'
        : locale === 'fr_FR'
        ? 'FR'
        : locale === 'es_ES'
        ? 'ES'
        : 'EN',
    [locale]
  );
  const [selectedPeriodicity, setSelectedPeriodicity] = useState(
    PERIODICITY.YEARLY
  );

  const onSelectDropshippingTab = useCallback(() => {
    setSelectedTab(TABS.DROPSHIPPING);
    router.push({
      pathname: router.pathname,
      query: { tab: 'dropshipping' },
    });
  }, [router]);

  const onSelectTurnkeyTab = useCallback(() => {
    setSelectedTab(TABS.TURNKEY);
    router.push({
      pathname: router.pathname,
      query: { tab: 'turnkey' },
    });
  }, [router]);

  const [plans, setPlans] = useState([]);
  const [radioOptions, setRadioOptions] = useState(INITIAL_RADIO_OPTIONS);

  useEffect(() => {
    if (user.channelCode === 'wix') {
      router.push(`/wix/pricing/${catalogs?.[0]?._id}`);
    }
  }, [catalogs, router, user.channelCode]);

  useEffect(() => {
    if (!plans.length) {
      const params = {};
      if (type) {
        params.online = false;
        params.q = type;
      }
      dispatch(
        getPlans(
          params,
          false,
          (data) => {
            const _plans = [];
            data?.items?.forEach((item) => {
              item?.models?.forEach((model) =>
                _plans?.push({
                  ...model,
                  name: item.name,
                  planCode: item.code,
                  planId: item._id,
                })
              );
            });
            if (!type) {
              _plans.push(shopify);
            }
            setPlans(_plans);
          },
          currency
        )
      );
    }
  }, [currency, dispatch, plans.length, type]);

  const promoPriceDropshipping = plans.some(
    (plan) =>
      plan?.streetPrice !== null &&
      plan?.attributes?.turnkey === false &&
      plan?.size === 'year'
  );
  const promoPriceTurnkey = plans.some(
    (plan) =>
      plan?.streetPrice !== null &&
      plan?.attributes?.turnkey === true &&
      plan?.size === 'year'
  );

  useEffect(() => {
    let tempRadioOptions = INITIAL_RADIO_OPTIONS;
    plans.forEach((plan) => {
      let type = plan?.attributes?.turnkey ? 'turnkey' : 'dropshipping';
      tempRadioOptions[type][plan.size] = true;
    });
    const switchToYearly = plans.some(
      (plan) =>
        plan?.streetPrice !== null &&
        plan?.attributes?.turnkey === false &&
        plan?.size === 'year'
    );
    if (selectedTab === TABS.DROPSHIPPING) {
      setSelectedPeriodicity(
        switchToYearly ? PERIODICITY.YEARLY : PERIODICITY.MONTHLY
      );
    } else {
      setSelectedPeriodicity(PERIODICITY.YEARLY);
    }
    setRadioOptions(tempRadioOptions);
  }, [plans, selectedTab, switchYearly]);

  const getRadioGroupTabs = useMemo(() => {
    let tabs = [];
    if (
      radioOptions?.dropshipping?.month ||
      !radioOptions?.bsocial?.month ||
      !radioOptions?.turnkey?.month
    ) {
      tabs.push({
        id: PERIODICITY.MONTHLY,
        label: TRANSLATIONS.pricing.monthly,
      });
    }
    if (
      radioOptions?.dropshipping?.year ||
      !radioOptions?.bsocial?.year ||
      !radioOptions?.turnkey?.year
    ) {
      tabs.push({
        id: PERIODICITY.YEARLY,
        label: TRANSLATIONS.pricing.yearly,
      });
    }
    return tabs;
  }, [
    TRANSLATIONS.pricing.monthly,
    TRANSLATIONS.pricing.yearly,
    radioOptions?.bsocial?.month,
    radioOptions?.bsocial?.year,
    radioOptions?.dropshipping?.month,
    radioOptions?.dropshipping?.year,
    radioOptions?.turnkey?.month,
    radioOptions?.turnkey?.year,
  ]);

  const plans_ = plans
    .filter((plan) => {
      switch (selectedTab) {
        case TABS.TURNKEY:
          return (
            plan?.attributes?.turnkey &&
            (selectedPeriodicity ? plan.size === 'year' : plan.size === 'month')
          );
        case TABS.DROPSHIPPING:
          return (
            !plan?.attributes?.turnkey &&
            (selectedPeriodicity ? plan.size === 'year' : plan.size === 'month')
          );
        case TABS.BSOCIAL:
          return (
            plan?.name === 'BSocial' &&
            (selectedPeriodicity ? plan.size === 'year' : plan.size === 'month')
          );
        default:
          return (
            plan?.attributes?.dropshipping &&
            (selectedPeriodicity ? plan.size === 'year' : plan.size === 'month')
          );
      }
    })
    .map((plan) => plan);

  const [goingDown, setgoingDown] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 500) {
        setgoingDown(true);
      } else {
        setgoingDown(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [goingDown]);

  return (
    <div className="pricing-container">
      <div className="d-f ai-c jc-c">
        {promoActive && (
          <Promo language={language} plansSetting={plansSetting} />
        )}
      </div>
      <div className={`pricing-head mt-md`} />
      <div className="pricing-content">
        <div className="tabs-container">
          <div
            className={`tab ${
              selectedTab !== TABS.DROPSHIPPING && 'tab-inactive'
            }`}
            onClick={onSelectDropshippingTab}
          >
            <span
              className={`tab-text ${
                selectedTab !== TABS.DROPSHIPPING && 'tab-text-inactive'
              }`}
            >
              {TRANSLATIONS.pricing.tab1}
            </span>
          </div>
          {plans?.find((plan) => plan?.attributes?.turnkey) && (
            <div
              className={`tab ${
                selectedTab !== TABS.TURNKEY && 'tab-inactive'
              }`}
              onClick={onSelectTurnkeyTab}
            >
              <span
                className={`tab-text d-f fd-c ${
                  selectedTab !== TABS.TURNKEY && 'tab-text-inactive'
                }`}
              >
                {TRANSLATIONS.pricing.tab2}
              </span>
            </div>
          )}
          <div className="section-wrap" />
        </div>
        <div className="row">
          {selectedTab === TABS.DROPSHIPPING && (
            <DropshippingPlan
              plan={plans_}
              plans={plans}
              goingDown={goingDown}
              getRadioGroupTabs={getRadioGroupTabs}
              selectedPeriodicity={selectedPeriodicity}
              setSelectedPeriodicity={setSelectedPeriodicity}
              promoPrice={promoPriceDropshipping}
            />
          )}
          {selectedTab === TABS.TURNKEY && (
            <TurnkeyPlan
              goingDown={goingDown}
              plan={plans_}
              getRadioGroupTabs={getRadioGroupTabs}
              selectedPeriodicity={selectedPeriodicity}
              setSelectedPeriodicity={setSelectedPeriodicity}
              promoPrice={promoPriceTurnkey}
            />
          )}
        </div>
        {false && <BannerInfo selectedTab={selectedTab} />}
        {(lang === 'it_IT' || lang === 'en_US') &&
          (user?.roles?.[0].name === 'free' || !isLoggedIn) && (
            <div>
              <Button
                className="primary wide large mb-40"
                label={TRANSLATIONS.pricing.bookACallLong}
                onClick={() => {
                  window.open(
                    'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ05yuGDnSnaQVPcB7NftLvgc_jP2xgLcnPkoFNwCug49YoNFGDOdSFtgLi2d6Aleq9tyV49ZXW1',
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              />
            </div>
          )}
        <UpsellAidify />
        <Faq selectedTab={selectedTab} />
      </div>
      <style jsx>{style}</style>
    </div>
  );
}
